import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ButtonV2, CopyButton, LinkIcon, Tooltip } from '@asigna/kit';
import { getCreatedPermissions } from 'api';
import cx from 'classnames';
import { AddContact, GradientCheckBox, NotificationCard, OwnerAvatar } from 'components';
import { PERMISSION_SIGNING_MESSAGE } from 'constants/base';
import { useActivateSafeFromUrlParam, useIsProActivated, useReadOnlyWarning } from 'hooks';
import useAddress from 'hooks/use-address';
import { useBreakpoints } from 'hooks/use-breakpoints';
import useNetwork from 'hooks/use-network';
import useData, { PROVIDERS } from 'hooks/useData';
import { useWalletProvider } from 'hooks/useWalletProvider';
import AddUserIcon from 'images/icon-adduser.svg';
import { useAtom } from 'jotai';
import { toast } from 'sonner';
import { useCurrentSafe, usePermissionList } from 'store';
import { contactsAtom } from 'store/contacts';
import { IEditWalletPermissions, PermissionType } from 'types';
import { getAccountExplorerUrl, isEqualPermissions, shortenAddress } from 'utils/helper';

import checkIcon from './utils/images/check.svg';
import errorIcon from './utils/images/error.svg';

import s from './index.module.scss';

export const OwnersPage: React.FC = () => {
  useActivateSafeFromUrlParam();

  const [network] = useNetwork();
  const history = useHistory();

  // const { safe } = useSafe();
  const address = useAddress() || '';
  const [contacts] = useAtom(contactsAtom);
  const { getContacts, signPermissionsMessage } = useData();

  const { currentSafe } = useCurrentSafe();
  const [isAddContactOpened, setIsAddContactOpened] = useState('');
  const owners = currentSafe?.owners;
  const permissions = currentSafe?.permissions;
  const [hoveredCard, setHoverCard] = useState('');
  const [hoveredCheckBox, setHoverCheckBox] = useState('');
  const readOnlyWarning = useReadOnlyWarning();
  const isReadOnly = Boolean(readOnlyWarning);

  const { onMobileZone } = useBreakpoints();
  const isActivatedPro = useIsProActivated();
  const { setPermissionList } = usePermissionList();
  const walletProvider = useWalletProvider();

  const [isChangedPermissions, setChangedPermissions] = useState(false);
  const [ownersPermissions, setOwnersPermissions] = useState<Record<string, PermissionType[]>>(
    {} as Record<string, PermissionType[]>
  );
  const [initOwnersPermissions, setInitOwnersPermissions] = useState<
    Record<string, PermissionType[]>
  >({} as Record<string, PermissionType[]>);

  useEffect(() => {
    if (isChangedPermissions) return;

    const ttt = permissions?.reduce(
      (acc, owner) => {
        acc[owner.address] = owner.permissions;

        return acc;
      },
      {} as Record<string, PermissionType[]>
    );

    setOwnersPermissions(ttt || ({} as Record<string, PermissionType[]>));
    setInitOwnersPermissions(ttt || ({} as Record<string, PermissionType[]>));
  }, [permissions]);

  const togglePermission = (address: string, permission: PermissionType) => {
    setChangedPermissions(true);
    setOwnersPermissions((prev) => {
      const currentPermissions = prev[address] || [];
      const hasPermission = currentPermissions.includes(permission);

      return {
        ...prev,
        [address]: hasPermission
          ? currentPermissions.filter((p) => p !== permission)
          : [...currentPermissions, permission],
      };
    });
  };

  const countExecutor = useMemo(() => {
    return Object.values(ownersPermissions).filter((permissions) => permissions.includes('EXECUTE'))
      .length;
  }, [ownersPermissions]);

  const countCreator = useMemo(() => {
    return Object.values(ownersPermissions).filter((permissions) => permissions.includes('PROPOSE'))
      .length;
  }, [ownersPermissions]);

  const countSigner = useMemo(() => {
    return Object.values(ownersPermissions).filter((permissions) => permissions.includes('VOTE'))
      .length;
  }, [ownersPermissions]);

  const disabledHint = isReadOnly ? readOnlyWarning : undefined;

  const disabledExecuteHint =
    countExecutor === 0
      ? 'Not enough executers — add more. The number of executers must be more than 0'
      : undefined;
  const disabledSignHint =
    countSigner < currentSafe?.threshold || 0
      ? 'Not enough signers  — add more. The number of signers must be no less than the threshold'
      : undefined;
  const disabledCreateHint =
    countCreator === 0
      ? 'Not enough creators — add more. The number of creators must be more than 0'
      : undefined;

  const onSign = async () => {
    currentSafe &&
      getCreatedPermissions(currentSafe.address, `stx:${network}`).then(setPermissionList);
  };

  const handleConfirm = async () => {
    if (currentSafe?.address) {
      const permissionsData = Object.entries(ownersPermissions).map(
        ([ownerAaddress, ownerPermissions]) => ({
          address: ownerAaddress,
          processType: 'EDIT',
          permissions: ownerPermissions,
        })
      );

      const messageString =
        PERMISSION_SIGNING_MESSAGE +
        JSON.stringify({
          permissionsData,
          timestamp: Date.now(),
        });

      // if (walletProvider) {
      await signPermissionsMessage(
        currentSafe.address,
        messageString,
        walletProvider?.provider as PROVIDERS,
        async () => {
          onSign();

          toast(
            () => (
              <NotificationCard
                title="Permission change request created"
                message="To change permissions, finalize the message signature request."
                type="success"
              />
            ),
            {
              className: 'w-full',
            }
          );
        },
        permissionsData as IEditWalletPermissions[]
      );
      // }
    }
  };

  if (!currentSafe || !owners) return <></>;

  const customHint = () => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        history.push(`/vault/${currentSafe.address}/pro`);
      }}
      className="absolute left-[-135px] w-[270px] text-r13 text-primary px-16 py-8 rounded-[6px] bg-[linear-gradient(91deg,_#1C190C_0%,_#14100C_100%)] border-[.5px] border-[#FFE341] z-10"
      disabled={isReadOnly}
    >
      <span className="text-white">Manage owners permissions easily with Asigna Pro.</span> Upgrade
      to Pro
    </button>
  );

  return (
    <div className="flex w-full pr-0 sm:pr-24 pl-0 h-screen pt-16 pb-0 sm:pb-16">
      <div className="flex flex-col rounded-[16px] sm:bg-black-1 w-full relative px-20 sm:px-32 pt-8 sm:pt-20 pb-24 overflow-y-scroll">
        <div
          className={cx(
            'flex items-center justify-between',
            isActivatedPro ? 'mb-16 sm:mb-8' : 'mb-24'
          )}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8">
            <h1 className="text-r24 text-white">Permissions</h1>
            <div className="flex gap-4 items-center">
              <p className="text-r14 text-regular">Threshold:</p>
              <p className="text-m14 text-white">
                {currentSafe.threshold}/{owners.length}
              </p>
            </div>
          </div>
          {isActivatedPro && (
            <ButtonV2
              message="Confirm"
              size="md"
              className="min-w-[132px]"
              disabled={
                countExecutor === 0 ||
                countCreator === 0 ||
                countSigner < currentSafe.threshold ||
                isEqualPermissions(initOwnersPermissions, ownersPermissions)
              }
              onClick={handleConfirm}
              disableHint={disabledExecuteHint || disabledSignHint || disabledCreateHint}
              hintPosition="bottom-end"
              hintClassName="max-w-[320px]"
            />
          )}
        </div>

        {isActivatedPro && (
          <div className="flex flex-wrap items-center gap-8 mb-32">
            <Tooltip
              content={disabledExecuteHint}
              position="bottom-start"
              className="max-w-[320px]"
            >
              <div
                className={cx(
                  'flex items-center gap-4 px-8 sm:px-12 h-32 border-[.5px] rounded-[8px] text-r12 sm:text-r13',
                  countExecutor > 0
                    ? 'border-success-30 bg-success-3 text-success'
                    : 'border-white-10 bg-white-3 text-regular'
                )}
              >
                <img src={countExecutor > 0 ? checkIcon : errorIcon} alt="" />
                <span>{countExecutor} Executer(s)</span>
              </div>
            </Tooltip>
            <Tooltip content={disabledCreateHint} position="bottom" className="max-w-[320px]">
              <div
                className={cx(
                  'flex items-center gap-4 px-8 sm:px-12 h-32 border-[.5px] rounded-[8px] text-r12 sm:text-r13',
                  countCreator > 0
                    ? 'border-success-30 bg-success-3 text-success'
                    : 'border-white-10 bg-white-3 text-regular'
                )}
              >
                <img src={countCreator > 0 ? checkIcon : errorIcon} alt="" />
                <span>{countCreator} Creator(s)</span>
              </div>
            </Tooltip>
            <Tooltip
              content={disabledSignHint}
              position={onMobileZone ? 'bottom' : 'bottom-end'}
              className="max-w-[320px]"
            >
              <div
                className={cx(
                  'flex items-center gap-4 px-8 sm:px-12 h-32 border-[.5px] rounded-[8px] text-r12 sm:text-r13',
                  countSigner >= currentSafe.threshold
                    ? 'border-success-30 bg-success-3 text-success'
                    : 'border-white-10 bg-white-3 text-regular'
                )}
              >
                <img src={countSigner >= currentSafe.threshold ? checkIcon : errorIcon} alt="" />
                <span>{countSigner} Signer(s)</span>
              </div>
            </Tooltip>
          </div>
        )}

        <div className="grid grid-cols-1">
          <div
            className={cx(
              'hidden sm:grid items-center text-m13 text-[#949494] font-semibold',
              'grid-cols-[35%_calc(65%-380px)_140px_130px_110px]'
            )}
          >
            <span>Name</span>
            <span>Address</span>
            <span className={s.gradientText}>Can execute Txn</span>
            <span className={s.gradientText}>Can create Txn</span>
            <span className={s.gradientText}>Can sign Txn</span>
          </div>
          {owners.map((owner, index) => {
            const name =
              address === owner.address
                ? 'Me'
                : contacts?.find((x) => x.contact_address === owner.address)?.contact_name ||
                  'Unknown';

            const permission = ownersPermissions[owner.address];
            if (onMobileZone) {
              return (
                <div
                  key={owner.address}
                  className="flex flex-col py-20 border-b-[.5px] border-black-4 gap-12"
                  onMouseEnter={() => setHoverCard(owner.address)}
                  onMouseLeave={() => setHoverCard('')}
                >
                  <div className="flex items-center gap-12">
                    <OwnerAvatar address={address} size="40px" index={index} />
                    <div className="flex flex-col">
                      <div className="flex items-center gap-4">
                        <p className="text-m14 text-white">{name}</p>
                        {onMobileZone && name === 'Unknown' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsAddContactOpened(owner.address);
                            }}
                            className="flex gap-4 items-center text-r12 text-primary"
                            disabled={isReadOnly}
                          >
                            <img src={AddUserIcon} alt="" /> Add
                          </button>
                        )}
                      </div>
                      <div className="flex flex-1 items-center gap-8">
                        <span className="text-r13 text-regular">
                          {shortenAddress(owner.address)}
                        </span>
                        <CopyButton copy={owner.address} />
                        <LinkIcon href={getAccountExplorerUrl(owner.address, network)} />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center text-r12 justify-start gap-16">
                    <div className="flex items-center gap-8">
                      <GradientCheckBox
                        checked={permission?.includes('EXECUTE') || false}
                        onChange={() => togglePermission(owner.address, 'EXECUTE')}
                        disabled={!isActivatedPro || isReadOnly}
                        disabledHint={disabledHint}
                        hintPosition="bottom-end"
                      />
                      <span className={s.gradientText}>Execute Txn</span>
                    </div>
                    <div className="flex items-center gap-8">
                      <GradientCheckBox
                        checked={permission?.includes('PROPOSE') || false}
                        onChange={() => togglePermission(owner.address, 'PROPOSE')}
                        disabled={!isActivatedPro || isReadOnly}
                        disabledHint={disabledHint}
                        hintPosition="bottom-end"
                      />
                      <span className={s.gradientText}>Create Txn</span>
                    </div>
                    <div className="flex items-center gap-8">
                      <GradientCheckBox
                        checked={permission?.includes('VOTE') || false}
                        onChange={() => togglePermission(owner.address, 'VOTE')}
                        disabled={!isActivatedPro || isReadOnly}
                        disabledHint={disabledHint}
                        hintPosition="bottom-end"
                      />
                      <span className={s.gradientText}>Sign Txn</span>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={owner.address}
                className={cx(
                  'flex sm:grid items-center relative',
                  'grid-cols-[35%_calc(65%-380px)_140px_130px_110px]',
                  'py-20 border-b-[.5px] border-black-4'
                )}
                onMouseEnter={() => setHoverCard(owner.address)}
                onMouseLeave={() => setHoverCard('')}
              >
                <div className="flex items-center gap-12">
                  <OwnerAvatar address={address} size="40px" index={index} />
                  <div className="flex items-center gap-4">
                    <p className="text-m14 text-white">{name}</p>
                    {hoveredCard === owner.address && name === 'Unknown' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAddContactOpened(owner.address);
                        }}
                        className="flex gap-4 items-center text-r12 text-primary"
                        disabled={isReadOnly}
                      >
                        <img src={AddUserIcon} alt="" /> Add
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-1 items-center gap-8">
                  <span className="text-r13 text-regular">{shortenAddress(owner.address)}</span>
                  <CopyButton copy={owner.address} />
                  <LinkIcon href={getAccountExplorerUrl(owner.address, network)} />
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setHoverCheckBox('execute-' + owner.address)}
                  onMouseLeave={() => setHoverCheckBox('')}
                >
                  <GradientCheckBox
                    checked={permission?.includes('EXECUTE') || false}
                    onChange={() => togglePermission(owner.address, 'EXECUTE')}
                    disabled={isReadOnly || !isActivatedPro}
                    disabledHint={disabledHint}
                    hintPosition="bottom-end"
                  />
                  {hoveredCheckBox === 'execute-' + owner.address && !isActivatedPro && (
                    <>{customHint()}</>
                  )}
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setHoverCheckBox('propose-' + owner.address)}
                  onMouseLeave={() => setHoverCheckBox('')}
                >
                  <GradientCheckBox
                    checked={permission?.includes('PROPOSE') || false}
                    onChange={() => togglePermission(owner.address, 'PROPOSE')}
                    disabled={isReadOnly || !isActivatedPro}
                    disabledHint={disabledHint}
                    hintPosition="bottom-end"
                  />
                  {hoveredCheckBox === 'propose-' + owner.address && !isActivatedPro && (
                    <>{customHint()}</>
                  )}
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setHoverCheckBox('vote-' + owner.address)}
                  onMouseLeave={() => setHoverCheckBox('')}
                >
                  <GradientCheckBox
                    checked={permission?.includes('VOTE') || false}
                    onChange={() => togglePermission(owner.address, 'VOTE')}
                    disabled={isReadOnly || !isActivatedPro}
                    disabledHint={disabledHint}
                    hintPosition="bottom-end"
                  />
                  {hoveredCheckBox === 'vote-' + owner.address && !isActivatedPro && (
                    <>{customHint()}</>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {Boolean(isAddContactOpened) && (
        <AddContact
          open={Boolean(isAddContactOpened)}
          onClose={() => setIsAddContactOpened('')}
          editingContact={null}
          presetWallet={isAddContactOpened}
          fetchContacts={getContacts}
        />
      )}
    </div>
  );
};
